import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, take } from 'rxjs/operators';
import { AldenActionTypes, DeleteElementsAction, DocumentLocationAction, DownloadWithWatermarkAction, ReloadCustomListTableAction, ReloadDataListElementsAction, SaveViewerConfigHeaderAction, ShowHideFiltersHeaderTableAction, UploadFileVersionAction, ViewAboutAction } from '../actions/alden.actions';
import { CustomListService } from '../../services/custom-list.service';
import { PipeServiceImpl } from 'pipe-service/pipe-service-impl';
import { SMTAboutDialogComponent } from '../../dialogs/about/about.dialog';
import { MatDialog } from '@angular/material/dialog';
import { AppStore, getAppSelection, NavigateToParentFolder } from '@alfresco/aca-shared/store';
import { AldenService } from '../../services/alden.service';
import { Store } from '@ngrx/store';

import { UploadFileVersionAction as AcaUploadFileVersionAction} from '@alfresco/aca-shared/store';
import { MinimalNodeEntity } from '@alfresco/js-api';
import { CustomListQueryBuilderService } from '../../services/custom-list-query-builder.service';
import { SMTLoadingDialogComponent } from '../../dialogs/loading/loading.dialog';
import { CustomUserPreferencesService } from '../../services/custom-user-preferences-service';
import { SMTConfigDialogComponent } from '../../dialogs/config/config.dialog';
import { DEFAULT_WATERMARK_PARAMS } from '../../models/alden-constants';

@Injectable()
export class SMTAldenEffects {
  constructor(
    private actions$: Actions,
    private customListService: CustomListService,
    private pipeService: PipeServiceImpl,
    private dialog: MatDialog,
    private store: Store<AppStore>,
    private aldenService: AldenService,
    private customListQueryBuilderService: CustomListQueryBuilderService,
    private customUserPreferencesService: CustomUserPreferencesService
  ) { }

  /**
   * Effect que escucha la acción ReloadCustomListTable y dispara
   * un evento para reiniar la tabla de customList.
   */
   reloadCustomListTable$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ReloadCustomListTableAction>(AldenActionTypes.ReloadCustomListTable),
        map((action) => {
          // Estudiar appHookService.reload en el ReloadCustomListTable
          if(action.delaySeconds) {
            setTimeout(() => {
              this.customListService.fireReloadTable(action.resetFilters, action.clearState);
              // Necesitamos limpiar los maps por si han actualizado algún valor
              this.pipeService.refreshMaps();
            }, action.delaySeconds * 1000); // Espera de X segundos
          } else {
              this.customListService.fireReloadTable(action.resetFilters, action.clearState);
              // Necesitamos limpiar los maps por si han actualizado algún valor
              this.pipeService.refreshMaps();
          }
        })
      ),
    { dispatch: false }
  );

  reloadDataListElements$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ReloadDataListElementsAction>(AldenActionTypes.ReloadDataListElements),
        map((action) => {
          this.customListService.fireLoadDataListElements(action.reset, action.resetOnlyDynamic);
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect que escucha la acción ShowHideFiltersHeaderTable y dispara
   * un evento en el servicio para mostrar u ocultar los filtros de la tabla de customList.
   */
  showHideFiltersHeaderTable$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ShowHideFiltersHeaderTableAction>(AldenActionTypes.ShowHideFiltersHeaderTable),
        map((_action) => {
          // Llama al servicio encargado de gestionar el estado de visibilidad de los filtros en la cabecera de la tabla
          this.customListService.fireShowHideFiltersHeaderTable();
        })
      ),
    { dispatch: false }
  );

   /**
   * Effect que escucha la acción ViewAbout y dispara
   * un evento para abrir el dialogo About.
   */
  viewAbout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ViewAboutAction>(AldenActionTypes.ViewAbout),
        map((_action) => {
          // Abre el dialogo
          this.openAboutDialogComponent();
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect que escucha la acción DeleteElements y dispara
   * un evento para eliminar un nodo seleccionado
   */
  deleteElements$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<DeleteElementsAction>(AldenActionTypes.DeleteElements),
        map((action) => {
          if (action && action.selection && action.selection.count > 0) {
            //Llamada a capa de servicio para eliminar el nodo seleccionado
            this.aldenService.deleteNode(action.selection.nodes);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && selection.count > 0) {
                  //Llamada a capa de servicio para eliminar el nodo seleccionado
                  this.aldenService.deleteNode(selection.nodes);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect que escucha la acción UploadFileVersion y dispara
   * un evento para el nodo seleccionado
   */
  uploadFileVersion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UploadFileVersionAction>(AldenActionTypes.UploadFileVersion),
        map((action) => {
          if (action && action.selection && action.selection.count > 0) {
            this.store.dispatch(new AcaUploadFileVersionAction(null));
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && selection.count > 0) {
                  this.store.dispatch(new AcaUploadFileVersionAction(null));
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect que escucha la acción DocumentLocationAction y dispara
   * un evento para el nodo seleccionado
   */
  documentLocation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<DocumentLocationAction>(AldenActionTypes.DocumentLocation),
        map((action) => {
          if (action && action.selection && action.selection.count > 0) {
            const node: MinimalNodeEntity = action.selection?.file;
            this.store.dispatch(new NavigateToParentFolder(node));
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && selection.count > 0) {
                  const node: MinimalNodeEntity = selection.file;
                  this.store.dispatch(new NavigateToParentFolder(node));
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect que escucha la acción SaveViewerConfigHeaderAction y dispara
   * un evento para guardar la configuración actual del usuario
   */
  saveViewerConfig$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SaveViewerConfigHeaderAction>(AldenActionTypes.SaveViewerConfig),
        map((_action) => {
          this.openConfigDialog()
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect que escucha la acción DownloadWithWatermarkACtiob y dispara
   * un evento para descargar un documento con marca de agua
   */
  downloadWithWatermark$ = createEffect(
    () => 
      this.actions$.pipe(
        ofType<DownloadWithWatermarkAction>(AldenActionTypes.DownloadWithWatermark),
        map((action) => {
          if (action && action.selection && action.selection.count > 0) {
            const node: MinimalNodeEntity = action.selection?.file;
            this.downloadWithWatermark(node.entry.id);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && selection.count > 0) {
                  const node: MinimalNodeEntity = selection.file;
                  this.downloadWithWatermark(node.entry.id);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );
  /**
   * Abre el cuadro de diálogo de información sobre la aplicación.
   * Este método centraliza la lógica de apertura del cuadro de diálogo de información sobre la aplicación.
   */
  private openAboutDialogComponent() {
    this.dialog.open(SMTAboutDialogComponent,
      {
        autoFocus: true,
        minWidth: '30%',
      },
    );
  }

      /**
   * Abre el cuadro de diálogo de guardado de búsquedas.
   * Este método centraliza la lógica de apertura del cuadro de diálogo de guardado de búsquedas.
   * Al dialogo se le proporciona la información de que visor está usando actualmente el usuario
   * En primer lugar, se cargan los ficheros de búsquedas guardadas y entonces se le proporcionan al dialogo, si existieran
   */
  private async openConfigDialog(){
    const dialogRef = this.dialog.open(SMTLoadingDialogComponent, { minWidth: '250px', disableClose: true });
    this.customUserPreferencesService
        .loadSearchFiles(this.customListQueryBuilderService.viewerConfig.title)
        .then(files => {
           dialogRef.close();
           this.dialog.open(SMTConfigDialogComponent,
            {
              data: {
                viewer: this.customListQueryBuilderService.viewerConfig.title,
                files: files,
                columns: this.customUserPreferencesService.columns
              },
              minWidth: '30%',
            },
          );
        });
  }

    /**
   * Descarga un fichero con marca de agua
   */
  private downloadWithWatermark(uuid:string){
    this.aldenService.downloadWatermarkedFile(uuid,DEFAULT_WATERMARK_PARAMS);
  }
}
